<div class="modal-body">
  <div class="modal-group">
    <div class="modal-group-title">Status</div>
    <div class="modal-row">
      <div class="modal-label">
        <label for="locationEnabled">Location status</label>
        <i class="fa-regular fa-circle-question fa-xl text-primary cursor-help"
           pTooltip="Deactivating a location stops all names from appearing in location dropdowns."></i>
      </div>
      <div class="state-switch">
        <span class="state-tag" [class.on]="!localData.enabled" (click)="localData.enabled = false">Inactive</span>
        <p-toggleswitch inputId="locationEnabled" [(ngModel)]="localData.enabled" />
        <span class="state-tag" [class.on]="localData.enabled" (click)="localData.enabled = true">Active</span>
      </div>
    </div>
  </div>

  <div class="modal-divider"></div>

  <div class="modal-group">
    <div class="modal-group-title">Location Names</div>

    <div class="modal-row">
      <div class="modal-label">
        <label for="defaultName">Default name<span class="req">*</span></label>
        <i class="fa-regular fa-circle-question fa-xl text-primary cursor-help"
           pTooltip="Default name is always enabled."></i>
      </div>
      @if (localData.id === 'new') {
        <input id="defaultName" type="text" pInputText [(ngModel)]="selectedDefaultName!.name"
               placeholder="Enter a default name..." class="flex-1 min-w-0" />
      } @else if (localData.names?.length) {
        <p-select
          inputId="defaultName"
          [(ngModel)]="selectedDefaultName"
          [options]="localData.names!"
          optionLabel="name"
          placeholder="Select default name..."
          appendTo="body"
          class="flex-1 min-w-0"
        />
      } @else {
        <span class="text-slate-400 italic">Add a name below first</span>
      }
    </div>

    <div class="modal-row">
      <div class="modal-label"><label for="newAltName">Alternate names</label></div>
      <div class="flex flex-1 min-w-0 gap-2.5 items-center">
        <input id="newAltName"
               type="text"
               pInputText
               [(ngModel)]="newAltName"
               placeholder="Add an alternative name..."
               (keydown.enter)="addAltName()"
               class="flex-1 min-w-0"
        />
        <p-button label="Add"
                  severity="secondary"
                  icon="fa-regular fa-plus"
                  [disabled]="!newAltName.trim()"
                  (onClick)="addAltName()"
        />
      </div>
    </div>

    <div class="alt-list">
      @for (nameOption of altNames; track nameOption) {
        <div class="modal-row alt-row">
          <div class="alt-name">
            <span>{{ nameOption.name }}</span>
            @if (canRemoveName(nameOption)) {
              <button type="button" class="alt-remove" [attr.aria-label]="'Remove ' + nameOption.name"
                      (click)="removeAltName(nameOption)">
                <i class="fa-solid fa-circle-xmark"></i>
              </button>
            }
          </div>
          <div class="state-switch">
            <span class="state-tag" [class.on]="!nameOption.enabled" (click)="nameOption.enabled = false">Inactive</span>
            <p-toggleswitch [(ngModel)]="nameOption.enabled" />
            <span class="state-tag" [class.on]="nameOption.enabled" (click)="nameOption.enabled = true">Active</span>
          </div>
        </div>
      } @empty {
        <p class="alt-empty">Location has no alternate names yet</p>
      }
    </div>
  </div>

  <div class="modal-divider"></div>

  <div class="modal-group">
    <div class="modal-group-title">Location Details</div>

    <div class="modal-row">
      <div class="modal-label">
        <label for="inRegion">In Region</label>
        @if (isOverseas) {
          <i class="fa-regular fa-circle-question fa-xl text-primary cursor-help"
             pTooltip="Overseas locations are always outside the CLC region."></i>
        }
      </div>
      <div class="state-switch">
        <!-- Same chips as the locations table; the unselected one is dimmed -->
        <p-tag class="state-chip" severity="danger" rounded value="No"
               [class.off]="localData.inRegion" [class.disabled]="isOverseas" (click)="setInRegion(false)" />
        <p-toggleswitch inputId="inRegion" [(ngModel)]="localData.inRegion" [disabled]="isOverseas" />
        <p-tag class="state-chip" severity="info" rounded value="Yes"
               [class.off]="!localData.inRegion" [class.disabled]="isOverseas" (click)="setInRegion(true)" />
      </div>
    </div>

    <div class="modal-row">
      <div class="modal-label"><label for="category">Category<span class="req">*</span></label></div>
      <p-select
        inputId="category"
        [(ngModel)]="localData.category"
        (onChange)="onCategoryChange()"
        [options]="categoryOptions"
        optionLabel="label"
        optionValue="value"
        placeholder="Select a category..."
        appendTo="body"
        class="flex-1 min-w-0"
      />
    </div>

    <div class="modal-row">
      <div class="modal-label">
        <label for="country">Country@if (isOverseas) {<span class="req">*</span>}</label>
      </div>
      <p-select
        inputId="country"
        [(ngModel)]="localData.countryId"
        [options]="countryOptions"
        optionLabel="name"
        optionValue="id"
        placeholder="Select a country..."
        [disabled]="!isOverseas"
        [filter]="true"
        filterBy="name"
        appendTo="body"
        class="flex-1 min-w-0"
      />
    </div>

    <!-- Lat/Long share a row; "Lat" lines up with the fields above -->
    <div class="modal-row">
      <div class="modal-label"></div>
      <div class="coords flex-1 min-w-0">
        <label for="lat" title="Latitude">Lat</label>
        <input id="lat" type="number" pInputText [(ngModel)]="localData.latitude"
               placeholder="e.g. -31.7776" class="flex-1 min-w-0" />
        <label for="lng" title="Longitude" class="ml-9">Long</label>
        <input id="lng" type="number" pInputText [(ngModel)]="localData.longitude"
               placeholder="e.g. 115.7506" class="flex-1 min-w-0" />
      </div>
    </div>
  </div>

  <div class="modal-footer">
    <p-button label="Save" icon="fa-regular fa-save" [disabled]="!canSave" (click)="closeDialog(localData)" />
    <p-button label="Cancel" severity="danger" icon="fa-regular fa-cancel" (click)="closeDialog()" />
  </div>
</div>
